import React, { useState, useEffect } from 'react';
import { loadStripe } from '@stripe/stripe-js';
import { Elements } from '@stripe/react-stripe-js';
import CheckoutFormTester from './components/CheckoutFormTester';
import { auth } from './lib/firebase'; // Import auth to get current user

// ====================================================================
// THIS FILE IS FOR TESTING PURPOSES ONLY
// This is a copy of the Checkout component with test-specific API endpoints
// It automatically performs the complete flow for testing single-use card elimination
// ====================================================================

// Make sure to call loadStripe outside of a component's render to avoid recreating the Stripe object on every render
const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY);

export default function CheckoutTester() {
  const [clientSecret, setClientSecret] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [uid, setUid] = useState(null);
  const [customerEmail, setCustomerEmail] = useState(null);
  const [stripeCustomerId, setStripeCustomerId] = useState(null);
  const [apiResponse, setApiResponse] = useState(null); // For debugging
  const [setupIntentId, setSetupIntentId] = useState(null);
  const [flowStep, setFlowStep] = useState('initialize'); // Track the current step in the flow
  const [flowStatus, setFlowStatus] = useState([]); // Track status of each step
  const [paymentMethodId, setPaymentMethodId] = useState(null); // To store payment method ID after setup
  const [autoFlow, setAutoFlow] = useState(false); // Control whether the flow should be automated

  useEffect(() => {
    // Listen for auth state changes to get the current user
    const unsubscribe = auth.onAuthStateChanged((user) => {
      if (user) {
        setUid(user.uid);
        setCustomerEmail(user.email);
        // Create/get customer first, then start the flow
        getOrCreateCustomer(user.uid, user.email);
      } else {
        // User is not logged in, handle accordingly
        setError('You must be logged in to access this page');
        setLoading(false);
      }
    });

    return () => unsubscribe(); // Cleanup subscription
  }, []);

  // This useEffect manages the flow progression after the initial setup
  useEffect(() => {
    if (!stripeCustomerId || !autoFlow) return; // Wait until we have a customer ID and automation is triggered
    
    const executeNextStep = async () => {
      switch (flowStep) {
        case 'setup_complete':
          // Standard setup intent completed, now cancel it
          addFlowStatus('Setup complete, proceeding to cancel');
          setFlowStep('canceling');
          await cancelSetupIntent();
          break;
          
        case 'canceled':
          // Setup intent canceled, proceed to $4.99 payment
          addFlowStatus('Setup canceled, proceeding to $4.99 payment');
          setFlowStep('fixed_payment');
          fetchSetupIntent499(uid, customerEmail, stripeCustomerId);
          break;
          
        case 'payment_complete':
          // $4.99 payment complete, flow is done
          addFlowStatus('$4.99 payment complete, flow is finished');
          setFlowStep('completed');
          setLoading(false);
          break;
          
        default:
          // Wait for other processes to update the flow step
          break;
      }
    };
    
    executeNextStep();
  }, [flowStep, stripeCustomerId, autoFlow]);

  const addFlowStatus = (message) => {
    setFlowStatus(prev => [...prev, `${new Date().toLocaleTimeString()}: ${message}`]);
  };

  const getOrCreateCustomer = (userId, email) => {
    // Using test-specific API endpoint
    fetch('/api/tester/get-or-create-customer', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ uid: userId, email })
    })
      .then(res => {
        if (!res.ok) {
          throw new Error(`API returned status ${res.status}`);
        }
        return res.json();
      })
      .then(data => {
        console.log('Customer data (tester):', data);
        addFlowStatus('Customer retrieved/created');
        if (data.customerId) {
          setStripeCustomerId(data.customerId);
          setLoading(false); // Set loading to false to allow user to click "Start Trial"
          setFlowStep('customer_ready');
        } else {
          throw new Error('No customer ID returned');
        }
      })
      .catch(err => {
        console.error('Error getting/creating customer (tester):', err);
        setError(`Failed to initialize customer: ${err.message}`);
        addFlowStatus(`Error: ${err.message}`);
        setLoading(false);
      });
  };

  // User initiates the first step
  const handleStartTrial = () => {
    setLoading(true);
    addFlowStatus('User initiated checkout process');
    setFlowStep('standard_setup');
    fetchSetupIntent(uid, customerEmail, stripeCustomerId);
  };

  const fetchSetupIntent = (userId, email, customerId) => {
    // Create a setup intent to get the client secret
    // Using test-specific API endpoint
    fetch('/api/tester/create-setup-intent', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        userId,
        email,
        customerId // Pass customer ID to setup intent
      })
    })
      .then((res) => {
        if (!res.ok) {
          throw new Error(`API returned status ${res.status}`);
        }
        return res.json();
      })
      .then((data) => {
        console.log('Setup intent response (tester):', data);
        setApiResponse(data);
        addFlowStatus('Standard setup intent created');
        
        if (!data.clientSecret) {
          throw new Error('API response missing clientSecret');
        }
        
        setClientSecret(data.clientSecret);
        // Store setup intent ID if available from response
        if (data.setupIntentId) {
          setSetupIntentId(data.setupIntentId);
        }
        
        // Wait for the payment method to be provided via CheckoutForm
        // The form will call onPaymentMethodCreated when done
      })
      .catch((err) => {
        console.error('Error fetching client secret (tester):', err);
        setError(`Failed to initialize payment: ${err.message}`);
        addFlowStatus(`Error creating setup intent: ${err.message}`);
        setLoading(false);
      });
  };

  // This function is called by the CheckoutForm when a payment method is successfully set up
  const onPaymentMethodCreated = (paymentMethod) => {
    setPaymentMethodId(paymentMethod.id);
    addFlowStatus(`Payment method created: ${paymentMethod.id}`);
    setFlowStep('setup_complete');
    // After first payment is complete, enable automatic flow for the rest of the process
    setAutoFlow(true);
  };

  const cancelSetupIntent = async () => {
    if (!setupIntentId) {
      const error = 'No setup intent ID available to cancel.';
      setError(error);
      addFlowStatus(`Error: ${error}`);
      return;
    }

    try {
      const response = await fetch('/api/tester/cancel-setup-intent', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          setupIntentId,
          customerId: stripeCustomerId,
          userId: uid
        })
      });
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || `API returned status ${response.status}`);
      }
      
      const data = await response.json();
      console.log('Cancel setup intent response:', data);
      setApiResponse(data);
      setClientSecret(''); // Clear client secret to reset the form
      addFlowStatus('Setup intent canceled successfully');
      
      // Move to next step
      setFlowStep('canceled');
      
    } catch (err) {
      console.error('Error canceling setup intent:', err);
      setError(`Failed to cancel setup intent: ${err.message}`);
      addFlowStatus(`Error canceling setup intent: ${err.message}`);
      setLoading(false);
    }
  };

  const fetchSetupIntent499 = (userId, email, customerId) => {
    // Create a setup intent with a $4.99 payment
    fetch('/api/tester/create-setup-intent-499', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        userId,
        email,
        customerId
      })
    })
      .then((res) => {
        if (!res.ok) {
          throw new Error(`API returned status ${res.status}`);
        }
        return res.json();
      })
      .then((data) => {
        console.log('Setup intent $4.99 response (tester):', data);
        setApiResponse(data);
        addFlowStatus('$4.99 payment intent created');
        
        if (!data.clientSecret) {
          throw new Error('API response missing clientSecret');
        }
        
        setClientSecret(data.clientSecret);
        // Store setup intent ID if available from response
        if (data.paymentIntentId) {
          setSetupIntentId(data.paymentIntentId);
        }
        
        // Payment will be processed and we'll be notified via onPaymentComplete
      })
      .catch((err) => {
        console.error('Error fetching $4.99 client secret (tester):', err);
        setError(`Failed to initialize $4.99 payment: ${err.message}`);
        addFlowStatus(`Error creating $4.99 payment: ${err.message}`);
        setLoading(false);
      });
  };

  // This function is called by the CheckoutForm when the $4.99 payment is complete
  const onPaymentComplete = () => {
    addFlowStatus('$4.99 payment completed successfully');
    setFlowStep('payment_complete');
  };

  const appearance = {
    theme: 'night',
    variables: {
      colorPrimary: '#6366f1', // Indigo-500
      colorBackground: '#1f2937', // Gray-800
      colorText: '#f9fafb', // Gray-50
      colorDanger: '#ef4444', // Red-500
      fontFamily: 'system-ui, -apple-system, sans-serif',
      spacingUnit: '4px',
      borderRadius: '8px',
    },
  };

  // Only create options if we have a clientSecret
  const options = clientSecret ? {
    clientSecret,
    appearance,
  } : null;

  return (
    <div className="min-h-screen bg-gray-900 text-gray-100 py-8 px-4 sm:px-6 lg:px-8 flex items-center justify-center">
      <div className="w-full max-w-lg mx-auto">
        <h1 className="text-3xl font-extrabold text-center mb-4">
          TESTING: Single-Use Card Elimination Flow
        </h1>
        <div className="bg-gray-800 shadow rounded-lg p-5">
          {clientSecret ? (
            <Elements stripe={stripePromise} options={options}>
              <CheckoutFormTester 
                uid={uid} 
                customerEmail={customerEmail} 
                stripeCustomerId={stripeCustomerId} 
                onPaymentMethodCreated={onPaymentMethodCreated}
                onPaymentComplete={onPaymentComplete}
                isTestEnvironment={autoFlow} // Only auto-submit after the first step
              />
            </Elements>
          ) : (
            <div className="flex flex-col space-y-4">
              {loading ? (
                <div className="flex justify-center items-center h-32">
                  <svg className="animate-spin h-8 w-8 text-indigo-500" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                </div>
              ) : error ? (
                <div className="text-red-500 text-center p-4">
                  <p className="mb-2 font-semibold">{error}</p>
                  <p className="text-sm text-gray-400">
                    API Response: {JSON.stringify(apiResponse)}
                  </p>
                </div>
              ) : flowStep === 'completed' ? (
                <div className="text-center">
                  <p className="text-lg font-semibold">Flow completed</p>
                </div>
              ) : flowStep === 'customer_ready' ? (
                <div className="text-center">
                  <p className="text-lg mb-4">Ready to start testing flow</p>
                  <button
                    onClick={handleStartTrial}
                    className="w-full bg-indigo-600 hover:bg-indigo-700 text-white py-2 px-4 rounded"
                  >
                    Start Trial
                  </button>
                  <p className="mt-2 text-sm text-gray-400">
                    This will initiate the standard setup intent. The rest of the flow will be automated.
                  </p>
                </div>
              ) : null}
              
              {/* Flow Status Log */}
              <div className="mt-4 border border-gray-700 rounded p-3">
                <h3 className="text-md font-medium mb-2">Flow Status Log:</h3>
                <div className="text-sm text-gray-300 h-40 overflow-y-auto space-y-1">
                  {flowStatus.length === 0 ? (
                    <p className="text-gray-500 italic">No steps completed yet...</p>
                  ) : (
                    flowStatus.map((status, i) => (
                      <div key={i} className="border-b border-gray-700 pb-1 mb-1 last:border-0">
                        {status}
                      </div>
                    ))
                  )}
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
} 